import { Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Layout from './components/layout/Layout.tsx';
import PageLoader from './components/ui/PageLoader.tsx';

// Lazy loaded pages for code splitting and low memory usage
const HomePage = lazy(() => import('@pages/HomePage'));
const AboutPage = lazy(() => import('@pages/AboutPage'));
const ContactPage = lazy(() => import('@pages/ContactPage'));
const ProductsPage = lazy(() => import('@pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('@pages/ProductDetailPage'));
const BlogPage = lazy(() => import('@pages/BlogPage'));
const BlogPostPage = lazy(() => import('@pages/BlogPostPage'));
const CategoryPage = lazy(() => import('@pages/CategoryPage'));
const FAQPage = lazy(() => import('@pages/FAQPage'));
const ProcessPage = lazy(() => import('@pages/ProcessPage'));
const SearchPage = lazy(() => import('@pages/SearchPage'));
const NotFoundPage = lazy(() => import('@pages/NotFoundPage'));

// Preload critical pages
const preloadHome = () => {
  const HomeModule = import('@pages/HomePage');
  return HomeModule;
};

// Preload on hover or idle time
if (typeof window !== 'undefined') {
  requestIdleCallback(() => {
    preloadHome();
  });
}

const AdminApp = lazy(() => import('./admin'));

function AppRoutes() {
  const location = useLocation();
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes location={location} key={location.pathname.split('/')[1] === 'admin' ? 'admin' : location.pathname}>
        {/* Admin Routes - No Public Layout */}
        <Route path="/admin/*" element={<AdminApp />} />
        
        {/* Public Routes with Layout */}
        <Route path="/*" element={
          <Layout>
            <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                <Route path="/:lang?/" element={<HomePage />} />
                <Route path="/:lang?/about" element={<AboutPage />} />
                <Route path="/:lang?/contact" element={<ContactPage />} />
                <Route path="/:lang?/products" element={<ProductsPage />} />
                <Route path="/:lang?/products/:slug" element={<ProductDetailPage />} />
                <Route path="/:lang?/category/:categorySlug" element={<CategoryPage />} />
                <Route path="/:lang?/blog" element={<BlogPage />} />
                <Route path="/:lang?/blog/:slug" element={<BlogPostPage />} />
                <Route path="/:lang?/faq" element={<FAQPage />} />
                <Route path="/:lang?/process" element={<ProcessPage />} />
                <Route path="/:lang?/search" element={<SearchPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AnimatePresence>
          </Layout>
        } />
      </Routes>
    </Suspense>
  );
}

function App() {
  return <AppRoutes />;
}

export default App;
